import { useState } from "react";
import { Link } from "react-router-dom";
import { Avatar } from "@/components/ui/Display.jsx";
import Button from "@/components/ui/Button.jsx";
import { formatTime } from "@/lib/format.js";

/*
 * The "Late Starters" and "Didn't Start Work" lists — one component, because in
 * dashboard.html they differ only in what sits on the right of each row.
 *
 * Collapsed to `preview` rows with a "Show all" toggle, as the markup does: on a
 * bad morning these lists are the length of the team, and a dashboard that
 * scrolls for a screen and a half stops being glanceable.
 */
export default function PeopleList({ people = [], preview = 5, empty, right }) {
    const [expanded, setExpanded] = useState(false);

    if (!people.length) {
        return (
            <p className="py-8 text-center text-[13px] text-slate-400 dark:text-slate-500">
                {empty}
            </p>
        );
    }

    const shown = expanded ? people : people.slice(0, preview);
    const hidden = people.length - shown.length;

    return (
        <>
            <div className="flex flex-col">
                {shown.map((person) => (
                    <Link
                        key={person._id}
                        to={`/dashboard/${person._id}`}
                        className="flex min-w-0 items-center gap-2.5 rounded-lg px-2 py-2 transition hover:bg-slate-50 dark:hover:bg-slate-800/60"
                    >
                        <Avatar name={person.name} size="sm" />

                        <span className="min-w-0 flex-1">
                            <span className="block truncate text-[13px] font-medium">
                                {person.name}
                            </span>
                            <span className="block truncate text-[11px] text-slate-400 dark:text-slate-500">
                                {person.team || "No team"}
                            </span>
                        </span>

                        {right?.(person)}
                    </Link>
                ))}
            </div>

            {(hidden > 0 || expanded) && (
                <div className="mt-1.5 text-center">
                    <Button variant="link" onClick={() => setExpanded((e) => !e)}>
                        {expanded ? "Show fewer" : `Show all ${people.length} people`}
                    </Button>
                </div>
            )}
        </>
    );
}

/** Right-hand slot for the late list: when they actually started. */
export function StartedAt({ person }) {
    return (
        <span className="shrink-0 font-mono text-[12px] font-semibold text-amber-600 dark:text-amber-400">
            {formatTime(person.firstTimeEntry)}
        </span>
    );
}

/** Right-hand slot for the didn't-start list. */
export function NoEntry() {
    return (
        <span className="shrink-0 rounded-md bg-slate-100 px-1.5 py-0.5 text-[11px] font-medium text-slate-500 dark:bg-slate-800 dark:text-slate-400">
            No entry
        </span>
    );
}
